import { useState, useEffect } from "react";
import { useOutletContext, Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { PageHeader } from "../components/layout/PageHeader";
import { PageSkeleton } from "../components/layout/PageSkeleton";
import { 
  Plus, 
  BookOpen, 
  ArrowRight,
  Loader2,
  Calendar,
  Star,
  ToggleLeft,
  ToggleRight,
  Archive,
  RotateCcw,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { formatDate } from "../lib/utils";

const menuTypes = [
  { value: "regular", label: "Carta Regular" },
  { value: "seasonal", label: "Menú Estacional" },
  { value: "daily", label: "Menú del Día" },
  { value: "special", label: "Menú Especial" },
];

export default function MenusPage() {
  const { currentRestaurant } = useOutletContext();
  const navigate = useNavigate();
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [archivedMenus, setArchivedMenus] = useState([]);
  const [loadingArchived, setLoadingArchived] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    menu_type: "regular",
    is_active: true,
    price: ""
  });

  useEffect(() => {
    if (!currentRestaurant) return;
    fetchMenus();
  }, [currentRestaurant]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchMenus = async () => {
    try {
      const data = await api.getMenus(currentRestaurant.restaurant_id);
      setMenus(data);
    } catch (error) {
      console.error("Failed to fetch menus:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchArchived = async () => {
    setLoadingArchived(true);
    try {
      const data = await api.getArchivedMenus(currentRestaurant.restaurant_id);
      setArchivedMenus(data);
    } catch {
      toast.error("Error al cargar archivados");
    } finally {
      setLoadingArchived(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error("El nombre es requerido");
      return;
    }

    setCreating(true);
    try {
      const payload = { ...formData };
      if (formData.menu_type === "daily" && formData.price) {
        payload.price = parseFloat(formData.price);
      } else {
        delete payload.price;
      }
      const newMenu = await api.createMenu(currentRestaurant.restaurant_id, payload);
      setMenus([...menus, newMenu]);
      setDialogOpen(false);
      setFormData({ name: "", description: "", menu_type: "regular", is_active: true, price: "" });
      toast.success("Menú creado correctamente");

      // For daily menus, navigate directly to detail page for dish linking
      if (newMenu.menu_type === "daily") {
        navigate(`/app/menus/${newMenu.menu_id}`);
      }
    } catch (error) {
      toast.error(error.message || "Error al crear el menú");
    } finally {
      setCreating(false);
    }
  };

  const handleToggleMenu = async (menuId, currentIsActive) => {
    const newState = !currentIsActive;
    setMenus(prev => prev.map(m => m.menu_id === menuId ? { ...m, is_active: newState } : m));
    try {
      await api.updateMenu(menuId, { is_active: newState });
      toast.success(newState ? "Menú activado" : "Menú desactivado");
    } catch {
      setMenus(prev => prev.map(m => m.menu_id === menuId ? { ...m, is_active: currentIsActive } : m));
      toast.error("Error al cambiar el estado del menú");
    }
  };

  const handleArchive = async (menuId) => {
    try {
      await api.updateMenu(menuId, { archived: true, is_active: false });
      setMenus(prev => prev.filter(m => m.menu_id !== menuId));
      toast.success("Menú archivado");
    } catch {
      toast.error("Error al archivar");
    }
  };

  const handleRestore = async (menuId) => {
    try {
      await api.updateMenu(menuId, { archived: false });
      setArchivedMenus(prev => prev.filter(m => m.menu_id !== menuId));
      fetchMenus();
      toast.success("Menú restaurado");
    } catch {
      toast.error("Error al restaurar");
    }
  };

  const handleDeletePermanent = async (menuId, menuName) => {
    if (!window.confirm(`¿Eliminar "${menuName}" definitivamente? Esta acción no se puede deshacer.`)) return;
    try {
      await api.deleteMenuPermanent(menuId);
      setArchivedMenus(prev => prev.filter(m => m.menu_id !== menuId));
      toast.success("Menú eliminado definitivamente");
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const getMenuTypeIcon = (type) => {
    switch (type) {
      case "seasonal": return Calendar;
      case "daily": return Calendar;
      case "special": return Star;
      default: return BookOpen;
    }
  };

  if (loading) {
    return <PageSkeleton kpis={0} showTabs={false} rows={5} />;
  }

  return (
    <div className="space-y-6" data-testid="menus-page">
      {/* Header */}
      <PageHeader
        icon={<BookOpen className="w-5 h-5" />}
        title="Gestión de Menús"
        description="Administra las cartas de tu restaurante."
        breadcrumb={[{ label: "Carta y cocina" }, { label: "Menús" }]}
        actions={
          <>
            <Button
              variant="outline"
              onClick={() => { setShowArchived(!showArchived); if (!showArchived) fetchArchived(); }}
              data-testid="toggle-archived-btn"
            >
              <Archive className="w-4 h-4 mr-2" />
              {showArchived ? "Ocultar archivados" : "Ver archivados"}
            </Button>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-full" data-testid="create-menu-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Nuevo menú
                </Button>
              </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Crear nuevo menú</DialogTitle>
                <DialogDescription>
                  Añade un nuevo menú o carta a tu restaurante
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="menu-name">Nombre *</Label>
                  <Input
                    id="menu-name"
                    placeholder="Ej: Carta Principal, Menú del Día..."
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    data-testid="menu-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="menu-type">Tipo de menú</Label>
                  <Select 
                    value={formData.menu_type}
                    onValueChange={(v) => setFormData({ ...formData, menu_type: v })}
                  >
                    <SelectTrigger data-testid="menu-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {menuTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {formData.menu_type === "daily" && (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="menu-price">Precio del Menú del Día (€)</Label>
                      <Input
                        id="menu-price"
                        type="number"
                        step="0.01"
                        placeholder="Ej: 14.90"
                        value={formData.price}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                        data-testid="menu-price-input"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground bg-muted/50 p-2 rounded">
                      Se crearán automáticamente las categorías: Entrantes, Principales y Postres.
                      Podrás vincular platos de tus otros menús directamente.
                    </p>
                  </>
                )}

                <div className="space-y-2">
                  <Label htmlFor="menu-description">Descripción</Label>
                  <Textarea
                    id="menu-description"
                    placeholder="Descripción del menú..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <Label htmlFor="menu-active">Activo</Label>
                  <Switch
                    id="menu-active"
                    checked={formData.is_active}
                    onCheckedChange={(v) => setFormData({ ...formData, is_active: v })}
                  />
                </div>

                <div className="flex gap-3 justify-end">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" disabled={creating} data-testid="save-menu-btn">
                    {creating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Creando...
                      </>
                    ) : (
                      "Crear menú"
                    )}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </>
        }
      />

      {/* Archived menus */}
      {showArchived && (
        <Card className="border-dashed" data-testid="archived-section">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Archive className="w-4 h-4" />
              Menús archivados
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loadingArchived ? (
              <div className="flex justify-center py-4">
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              </div>
            ) : archivedMenus.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No hay menús archivados</p>
            ) : (
              <div className="space-y-2">
                {archivedMenus.map(menu => (
                  <div key={menu.menu_id} className="flex items-center justify-between p-3 rounded-lg border bg-muted/30" data-testid={`archived-${menu.menu_id}`}>
                    <div>
                      <p className="font-medium text-sm">{menu.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {menuTypes.find(t => t.value === menu.menu_type)?.label} — Archivado
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => handleRestore(menu.menu_id)} data-testid={`restore-${menu.menu_id}`}>
                        <RotateCcw className="w-3.5 h-3.5 mr-1" />
                        Restaurar
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => handleDeletePermanent(menu.menu_id, menu.name)} data-testid={`delete-perm-${menu.menu_id}`}>
                        <Trash2 className="w-3.5 h-3.5 mr-1" />
                        Eliminar
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Menus Grid */}
      {menus.length === 0 ? (
        <Card className="border-dashed border-2">
          <CardContent className="py-16 text-center">
            <BookOpen className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="font-heading text-lg font-semibold mb-2">No tienes menús aún</h3>
            <p className="text-muted-foreground mb-6">
              Crea tu primera carta para empezar a añadir platos
            </p>
            <Button onClick={() => setDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Crear primer menú
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {menus.map((menu) => {
            const TypeIcon = getMenuTypeIcon(menu.menu_type);
            return (
              <Card 
                key={menu.menu_id} 
                className="group hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                data-testid={`menu-card-${menu.menu_id}`}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                      <TypeIcon className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => { e.preventDefault(); handleArchive(menu.menu_id); }}
                        title="Archivar menú"
                        data-testid={`archive-${menu.menu_id}`}
                        className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                      >
                        <Archive className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => { e.preventDefault(); handleToggleMenu(menu.menu_id, menu.is_active); }}
                        data-testid={`toggle-menu-${menu.menu_id}`}
                        title={menu.is_active ? "Clic para desactivar" : "Clic para activar"}
                        className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1 rounded-full border transition-all hover:opacity-80 active:scale-95 ${
                          menu.is_active
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-muted text-muted-foreground border-border"
                        }`}
                      >
                        {menu.is_active
                          ? <><ToggleRight className="w-3.5 h-3.5" />Activo</>
                          : <><ToggleLeft className="w-3.5 h-3.5" />Inactivo</>
                        }
                      </button>
                    </div>
                  </div>
                  <CardTitle className="font-heading mt-4">{menu.name}</CardTitle>
                  <CardDescription>
                    {menu.description || menuTypes.find(t => t.value === menu.menu_type)?.label}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between text-sm text-muted-foreground mb-4">
                    <span>Creado {formatDate(menu.created_at)}</span>
                    {menu.menu_type === "daily" && menu.price > 0 && (
                      <span className="font-semibold text-primary">{menu.price.toFixed(2)} €</span>
                    )}
                  </div>
                  <Link to={`/app/menus/${menu.menu_id}`}>
                    <Button variant="outline" className="w-full group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                      Gestionar
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
